/**
 * Téléphonie (POC) – briques du socle partagées par les écrans : durée d'un appel, correspondant, état du résumé, et la
 * section « Appels » d'une fiche d'objet (appels dont le rattachement est confirmé) :
 *   <AppelsObjet ressource="tiers" objetId={tiers.id} filiale={tiers.filiale} />
 * Masquée sans droit « lire » sur service.telephonie.appels (module inactif dans la filiale : absent du catalogue).
 */
import { useQuery } from '@tanstack/react-query';
import { RESSOURCE_APPELS, type AppelLigne, type Page } from '@agora/shared';
import { StatusBadge } from '../../design';
import { ErreurApi, LienPage, useApi, useDroits, useFormats, useT, type Traduire } from '../../socle';

/** Durée d'un appel avec les unités Intl de la langue (« 4 min 5 s »). */
export function useDureeAppel(): (secondes: number) => string {
  const f = useFormats();
  return (s) => {
    const unite = (n: number, unit: 'minute' | 'second') => f.nombre(n, { style: 'unit', unit, unitDisplay: 'short' });
    const min = Math.floor(s / 60);
    const sec = s % 60;
    if (!min) return unite(sec, 'second');
    return sec ? `${unite(min, 'minute')} ${unite(sec, 'second')}` : unite(min, 'minute');
  };
}

/** Nom d'un contact reconnu (« Claire Exemple »). */
export const nomContactAppel = (c: NonNullable<AppelLigne['contact']>) => [c.prenom, c.nom].filter(Boolean).join(' ');

/** Correspondant affiché : personne et entreprise reconnues, sinon numéro, sinon « numéro masqué ». */
export const correspondantAppel = (a: Pick<AppelLigne, 'tiers' | 'contact' | 'numeroExterne'>, t: Traduire) =>
  [a.contact ? nomContactAppel(a.contact) : '', a.tiers?.raisonSociale].filter(Boolean).join(' · ')
  || a.numeroExterne || t('service.telephonie.numeroMasque');

/**
 * État du résumé : traitement automatique en cours ou en échec, résumé disponible, sans résumé, sans enregistrement.
 * Pas de validation (décision du DSI, 03/11/2026) : un appel sans résumé n'attend rien de l'utilisateur.
 */
export function BadgeResumeAppel({ appel }: { appel: Pick<AppelLigne, 'traitement' | 'resumeStatut'> }) {
  const { t } = useT();
  if (appel.traitement === 'transcription' || appel.traitement === 'resume') {
    return <StatusBadge status="progress" label={t(`service.telephonie.traitements.${appel.traitement}`)} />;
  }
  if (appel.traitement === 'erreur') return <StatusBadge status="danger" label={t('service.telephonie.traitements.erreur')} />;
  if (appel.resumeStatut !== null) return <StatusBadge status="success" label={t('service.telephonie.resumes.present')} />;
  if (appel.traitement === 'termine') return <StatusBadge status="neutral" label={t('service.telephonie.resumes.absent')} />;
  return <StatusBadge status="neutral" label={t('service.telephonie.traitements.aucun')} />;
}

export const routeAppel = (id: string) => `/service/telephonie/appels/${encodeURIComponent(id)}`;

export interface AppelsObjetProps {
  ressource: 'tiers';
  objetId: string;
  /** Filiale de l'objet (les appels sont lus dans cette filiale). */
  filiale: string;
}

export function AppelsObjet({ ressource, objetId, filiale }: AppelsObjetProps) {
  const { t } = useT();
  const f = useFormats();
  const api = useApi();
  const duree = useDureeAppel();
  const droits = useDroits(RESSOURCE_APPELS);
  const liste = useQuery({
    queryKey: ['appels', 'objet', ressource, objetId],
    enabled: droits.lire,
    queryFn: ({ signal }) => api.get<Page<AppelLigne>>('/v1/service/appels', { filiale, ressource, objetId, limite: 10 }, signal),
  });
  if (!droits.lire || (liste.error instanceof ErreurApi && liste.error.code === 'module_inactif')) return null;
  const elements = liste.data?.elements || [];

  return (
    <section className="ag-glass-card agx-carte agx-appels-objet" aria-label={t('service.telephonie.objet.titre')}>
      <div className="agx-appel-tete">
        <h2 className="agx-titre-carte">{t('service.telephonie.objet.titre')}</h2>
        <LienPage to="/service/telephonie/appels" label={t('service.telephonie.objet.tous')} />
      </div>
      {liste.isLoading ? <p className="agx-vide" role="status">{t('app.chargement')}</p> : elements.length ? (
        <ul className="agx-appels-liste">
          {elements.map((a) => (
            <li key={a.id} className="agx-appel-ligne">
              <span className="agx-puce">
                <LienPage to={routeAppel(a.id)} label={f.dateHeure(a.debut, a.filiale)} />
                <span>{t(`service.telephonie.sens.${a.sens}`)} · {duree(a.dureeS)}</span>
              </span>
              <BadgeResumeAppel appel={a} />
            </li>
          ))}
        </ul>
      ) : liste.error ? (
        <p className="agx-vide" role="alert">{t(`erreurs.${liste.error instanceof ErreurApi ? liste.error.code : 'interne'}`)}</p>
      ) : (
        <p className="agx-vide">{t('service.telephonie.objet.vide')}</p>
      )}
    </section>
  );
}
